import React, { useContext, useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { usePermissions } from "src/hooks/usePermissions";
import { FeedProvider, onFetchNextPageProps } from "src/context/Feed";
import { Dashboard } from "src/templates";
import { apiClient } from "../../api/apiService";
import ClaimDialog from "../../components/Form/Claim/ClaimDialog";
import HomeUserClaimDialog from "../../components/Form/Claim/HomeUserClaimDialog";
import { toRequestFiltersRestDTO } from "../../api/apiHelper";
import { AppContext } from "../../context/AppContext";
import { PageTitleSection } from "./PageTitleSection";
import ActionSectionResponsive from "./ActionSection";
import ContentSectionResponsive from "./ContentSection";
import {
  FilterPremisesDialog,
  mapRequestsToRows,
  PremiseRequest,
  RequestFilter,
  SortByEnum,
} from "src/components/Premises";
import { PremisesDashboardProps } from "./types";

export const PremisesDashboard: React.FC<PremisesDashboardProps> = ({
  pageTitleSection = <PageTitleSection />,
  "data-id": dataId = "premisesDashboard",
}: PremisesDashboardProps) => {
  const location = useLocation();
  const {
    context: { premiseDashboardFilters: appliedFilters, project },
    setContext,
  } = useContext(AppContext);

  // Use the custom hook for search functionality
  const { projectId } = useParams<{ projectId: string }>();
  const [claimDialogOpen, setClaimDialogOpen] = useState<boolean>(false);
  const { isHomeUser } = usePermissions();
  const [openFilterPremisesDialog, setOpenFilterPremisesDialog] =
    useState(false);

  const setAppliedFilters = (filters: RequestFilter) => {
    setContext({ premiseDashboardFilters: filters });
  };

  useEffect(() => {
    if (project && location.pathname === "/") {
      setContext({ project: undefined });
    }
  }, []);

  const handleFetchNextPage = async ({
    searchTerm,
    page,
    sortConfig,
  }: onFetchNextPageProps) => {
    const premisesUrl = projectId ? `/request/${projectId}` : "/request";
    const search = searchTerm && searchTerm.length > 0;
    const response = projectId
      ? await apiClient.get<{ requests: PremiseRequest[] }>(premisesUrl, {
          params: {
            page,
            limit: 10,
            sortDirection: sortConfig.direction,
            sortBy: sortConfig.key,
            searchTerm: search ? searchTerm : undefined,
          },
        })
      : await apiClient.post<{ requests: PremiseRequest[] }>(premisesUrl, {
          page,
          limit: 10,
          sortDirection: sortConfig.direction,
          sortBy: sortConfig.key,
          searchTerm: search ? searchTerm : undefined,
          filters:
            projectId || Object.values(appliedFilters).length === 0
              ? undefined
              : toRequestFiltersRestDTO(appliedFilters),
        });
    const newData = mapRequestsToRows(response.data.requests);
    const isEOL = response.data.requests.length === 0;
    return { newData, isEOL };
  };

  const filtersCount = Object.entries(appliedFilters ?? {}).filter(
    ([k, v]) => k !== "createdTo" && v
  ).length;

  const openClaimDialog = () => {
    setClaimDialogOpen(true);
  };

  return (
    <FeedProvider
      onFetchNextPage={handleFetchNextPage}
      initialState={{
        sortConfig: { key: SortByEnum.UPDATED, direction: "DESC" },
      }}
    >
      <Dashboard
        pageTitleSection={pageTitleSection}
        actionSection={
          <ActionSectionResponsive
            openClaimDialog={openClaimDialog}
            openFilterDialog={() => setOpenFilterPremisesDialog(true)}
            filtersCount={filtersCount}
          />
        }
        data-id={dataId}
      >
        <ContentSectionResponsive />
        {/* End Actions container */}
        {isHomeUser ? (
          <HomeUserClaimDialog
            open={claimDialogOpen}
            setOpen={setClaimDialogOpen}
          />
        ) : (
          <ClaimDialog open={claimDialogOpen} setOpen={setClaimDialogOpen} />
        )}
        {/* Filter Premises Dialog */}
        {openFilterPremisesDialog && (
          <FilterPremisesDialog
            open={openFilterPremisesDialog}
            setOpen={setOpenFilterPremisesDialog}
            appliedFilters={appliedFilters}
            setAppliedFilters={setAppliedFilters}
          />
        )}
        {/* End Filter Premises Dialog */}
      </Dashboard>
    </FeedProvider>
  );
};

export const Premises: React.FC = () => <PremisesDashboard />;
